import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { getNotifications, deleteNotification } from "../../api/notifications";

// component
import ConfirmDialog from '../ConfirmDialog';
import NotificationCard from './NotificationCard';

// mui
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import MenuItem from '@mui/material/MenuItem';
import Pagination from '@mui/material/Pagination';
import Paper from '@mui/material/Paper';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// types
type Props = {
    roomId: number,
    userId: number,
}

type NotificationType = {
    notificationId: number,
    notificationTime: string,
    isReaded: boolean,
    title: string | null,
    message: string,
    link: string | null,
}

function NotificationList(props: Props) {
    const { roomId, userId } = props;

    /**
     *  ソート
     */
    const [sort, setSort] = useState('');

    const fnChangeSort = useCallback((notificationList: NotificationType[], sort: string) => {
        if (sort === "date-desc") {
            sortDateDesc(notificationList);
        } else if (sort === "date-asc") {
            sortDateAsc(notificationList);
        } else {
            setNotificationList(notificationList);
        }
    }, []);

    const handleChangeSort = (event: SelectChangeEvent, notificationList: NotificationType[]) => {
        fnChangeSort(notificationList, event.target.value);
        setSort(event.target.value);
    }

    // 更新日（新しい順）
    const sortDateDesc = (notificationList: NotificationType[]) => {
        const tempNotificationList = [...notificationList].sort((a, b) => {
            const aDateMillis = new Date(a.notificationTime).getTime();
            const bDateMillis = new Date(b.notificationTime).getTime();
            return -(aDateMillis - bDateMillis);

        });
        setNotificationList(tempNotificationList);
    }

    // 更新日（古い順）
    const sortDateAsc = (notificationList: NotificationType[]) => {
        const tempNotificationList = [...notificationList].sort((a, b) => {
            const aDateMillis = new Date(a.notificationTime).getTime();
            const bDateMillis = new Date(b.notificationTime).getTime();
            return aDateMillis - bDateMillis;
        });
        setNotificationList(tempNotificationList);
    }

    /**
     * 通知の取得
     */
    const [notificationList, setNotificationList] = useState<NotificationType[]>([]);

    const fnGetNotifications = useCallback(async (roomId: number, userId: number) => {
        const result = await getNotifications(roomId, userId);
        if (result) {
            // fnChangeSort内でsetNotificationListしている
            fnChangeSort(result, sort);
        }
    }, [fnChangeSort, sort]);

    useEffect(() => {
        fnGetNotifications(roomId, userId);
    }, [roomId, userId, fnGetNotifications]);

    /**
     * 通知の削除
     */
    const handleDeleteNotification = (notificationId: number, isReaded: boolean) => {
        setTargetDeleteNotification(notificationId);
        setTargetDeleteNotificationIsReaded(isReaded);
        setIsOpenConfirmDialog(true);
    }
 
    // 削除確認ダイアログ
    const [targetDeleteNotification, setTargetDeleteNotification] = useState(0);
    const [targetDeleteNotificationIsReaded, setTargetDeleteNotificationIsReaded] = useState(false);
    const [isOpenConfirmDialog, setIsOpenConfirmDialog] = useState(false);

    const handleDeleteNotificationAgree = async () => {
        await deleteNotification(targetDeleteNotification, targetDeleteNotificationIsReaded)
            .then((result) => {
                if (result === "success") {
                    fnGetNotifications(roomId, userId);
                }
            });
    }

    const handleDeleteNotificationDisagree = () => {
        setTargetDeleteNotification(0);
        setTargetDeleteNotificationIsReaded(false);
    }

    /**
     *  ページネーション
     */
    const [page, setPage] = useState<number>(1); // ページ番号
    const [pageCount, setPageCount] = useState<number>(1); // ページ数
    const [displayedNotificationList, setdisplayedNotificationList] = useState<NotificationType[]>([]); // 表示データ
    const displayNum = 10; //1ページあたりの項目数

    useEffect(() => {
        if (notificationList) {
            //ページカウントの計算（今回は3項目/ページなので4ページ）
            setPageCount(Math.ceil(notificationList.length / displayNum));
            //表示データを抽出
            setdisplayedNotificationList(notificationList.slice(((page - 1) * displayNum), page * displayNum))
        }
    }, [notificationList, page]);

    const handleChangePagination = (event: React.ChangeEvent<unknown>, page: number) => {
        //ページ移動時にページ番号を更新
        setPage(page);
        //ページ移動時に表示データを書き換える
        setdisplayedNotificationList(notificationList.slice(((page - 1) * displayNum), page * displayNum))
    }

    return (
        <>
            {notificationList.length >= 1
                ? (<>
                    <Box
                        sx={{
                            maxWidth: 980,
                            mx: "auto",
                            pt: 3,
                        }}
                    >
                        <Stack
                            alignItems="center"
                            direction="row"
                            justifyContent="space-between"
                            spacing={1}
                            sx={{
                                flexWrap: { mb: "wrap", tb: "nowrap" }
                            }}
                        >
                            <Box
                                order={1}
                            >
                                <Typography variant='body2'>
                                    トピック数：{notificationList.length.toString()}
                                </Typography>
                            </Box>
                            <Box
                                order={{ mb: 3, tb: 2 }}
                                sx={{
                                    width: { mb: "100%", tb: "auto" }
                                }}
                            >
                                <Pagination
                                    count={pageCount}
                                    page={page}
                                    onChange={handleChangePagination}
                                    sx={{
                                        '& .MuiPagination-ul': {
                                            justifyContent: "center"
                                        }
                                    }}
                                />
                            </Box>
                            <Box
                                order={{ mb: 2, tb: 3 }}
                            >
                                <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                                    <InputLabel id="select-helper-label">並び替え</InputLabel>
                                    <Select
                                        labelId="select-helper-label"
                                        id="select-helper"
                                        value={sort}
                                        label="並び替え"
                                        onChange={(event) => handleChangeSort(event, notificationList)}
                                    >
                                        <MenuItem value="">
                                            <em>未選択</em>
                                        </MenuItem>
                                        <MenuItem value={"date-desc"}>更新日（新しい順）</MenuItem>
                                        <MenuItem value={"date-asc"}>更新日（古い順）</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                        </Stack>
                    </Box>
                    {/* 通知一覧 */ }
                    {notificationList &&
                        <List>
                            {displayedNotificationList.map((notification) => (
                                <ListItem
                                    key={notification.notificationId}
                                    sx={{
                                        p: 0,
                                        '& .MuiPaper-root': {
                                            borderTop: "1px solid",
                                            borderTopColor: (theme) => theme.palette.divider,
                                        },
                                        '&:first-of-type .MuiPaper-root': {
                                            borderTop: "unset",
                                        },
                                    }}
                                >
                                    <NotificationCard
                                        notificationId={notification.notificationId}
                                        notificationTime={notification.notificationTime}
                                        isReaded={notification.isReaded}
                                        title={notification.title}
                                        message={notification.message}
                                        link={notification.link}
                                        handleDeleteNotification={handleDeleteNotification}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    }
                </>): (
                    <Box sx={{mt: 1}}>
                        <Paper
                            sx={{
                                width: "calc(100% - 16px)",
                                maxWidth: 980,
                                mx: "auto",
                                px: { mb: 2, tb: 3 },
                                py: 2,
                                boxSizing: "border-box",
                                borderRadius: 0,
                                boxShadow: "unset",
                            }}
                        >通知はありません</Paper>
                    </Box>
                )
            }

            {/*削除確認ダイアログ*/}
            <ConfirmDialog
                agreeButtonDisplay={"削除する"}
                body={"この操作は取り消すことができません。本当に削除しますか？"}
                handleClickAgree={handleDeleteNotificationAgree}
                handleClickDisagree={handleDeleteNotificationDisagree}
                isOpen={isOpenConfirmDialog}
                setIsOpen={setIsOpenConfirmDialog}
                title={"削除を実行しますか？"}
            />
        </>
    )
}

export default React.memo(NotificationList);